import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  type User,
  onAuthStateChanged
} from 'firebase/auth';
import { localDbService } from '../../services/shared';
import { MentraUser, UserRole, ContentMode, Theme } from '../../shared/types/src';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../shared/config/src';

/**
 * Authentication context for Mentra frontend
 * Manages user authentication state and role-based access
 */

interface AuthContextType {
  user: MentraUser | null;
  loading: boolean;
  loadingAuth: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUserRole: (role: UserRole) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Separate hook definition
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Separate provider component
function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<MentraUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAndSetUser = useCallback(async (firebaseUser: User | null) => {
    setLoadingAuth(true);
    try {
      if (firebaseUser) {
        // Get user data from Firestore with timeout and retry logic
        let userData = null;
        let userDoc = null;
        
        try {
          const userRef = doc(db, 'users', firebaseUser.uid);
          userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            userData = userDoc.data();
          }
        } catch (firestoreError) {
          console.warn('Firestore connection failed:', firestoreError);
          // Create a basic user from Firebase auth when Firestore is unavailable
          const mentraUser = Object.create(
            Object.getPrototypeOf(firebaseUser),
            Object.getOwnPropertyDescriptors(firebaseUser)
          ) as MentraUser;
          
          // Add basic Mentra properties
          Object.assign(mentraUser, {
            role: 'patient' as UserRole,
            free_access: false,
            daily_token_limit: 100,
            tokens_used_today: 0,
            contentMode: 'FULL' as ContentMode,
            theme_preference: 'light' as Theme,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            therapist_request_pending: false,
            demo_enabled: false,
            last_active: new Date().toISOString(),
            therapist_id: null,
            therapistIds: []
          });
          
          // Set the role in sessionStorage for API calls
          sessionStorage.setItem('activeUserRole', 'patient');
          
          setUser(mentraUser);
          setLoadingAuth(false);
          setIsInitialized(true);
          setLoading(false);
          return;
        }

        const role: UserRole = userData?.role || 'standard_user';
        sessionStorage.setItem('activeUserRole', role);

        // Create MentraUser by extending the Firebase User object
        const mentraUser = Object.create(
          Object.getPrototypeOf(firebaseUser),
          Object.getOwnPropertyDescriptors(firebaseUser)
        ) as MentraUser;
        
        // Add Mentra-specific properties
        Object.assign(mentraUser, {
          role: role,
          free_access: userData?.free_access || false,
          daily_token_limit: userData?.daily_token_limit || 20000,
          tokens_used_today: userData?.tokens_used_today || 0,
          content_mode: userData?.content_mode || 'FULL' as ContentMode,
          theme_preference: userData?.theme_preference || 'light' as Theme,
          created_at: userData?.created_at,
          updated_at: userData?.updated_at,
          therapist_request_pending: userData?.therapist_request_pending || false,
          demo_enabled: userData?.demo_enabled || false,
          last_active: userData?.last_active,
          therapist_id: userData?.therapist_id
        });
        
        setUser(mentraUser);
        
        // If we don't have user data from Firestore, try to create it in the background
        if (!userDoc?.exists()) {
          try {
            const userRef = doc(db, 'users', firebaseUser.uid);
            const defaultUserData = {
              email: firebaseUser.email,
              role: 'standard_user' as UserRole,
              free_access: false,
              daily_token_limit: 20000,
              tokens_used_today: 0,
              content_mode: 'FULL' as ContentMode,
              theme_preference: 'light' as Theme,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            await setDoc(userRef, defaultUserData);
          } catch (createError) {
            console.warn('Failed to create user document in Firestore:', createError);
            // This is not critical - user can still use the app
          }
        }
      } else {
        setUser(null);
        sessionStorage.removeItem('activeUserRole');
      }
    } catch (error) {
      console.error('Error in auth state change:', error);
      setError('Failed to load user data');
      // In production, do NOT set a fallback user. Just set user to null.
      setUser(null);
      sessionStorage.removeItem('activeUserRole');
    } finally {
      setLoadingAuth(false);
      setIsInitialized(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        await fetchAndSetUser(firebaseUser);
      } catch (error) {
        console.error('Auth state change error:', error);
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [fetchAndSetUser]);

  const refreshUser = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;
    
    setLoadingAuth(true);
    try {
      // Get user data from Firestore with error handling
      let userData = null;
      
      try {
        const userRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          userData = userDoc.data();
        }
      } catch (firestoreError) {
        console.warn('Firestore connection failed during refresh, using current user data:', firestoreError);
        // Use current user data as fallback
        if (user) {
          userData = {
            role: user.role,
            free_access: user.free_access,
            daily_token_limit: user.daily_token_limit,
            tokens_used_today: user.tokens_used_today,
            content_mode: user.contentMode,
            theme_preference: user.theme_preference,
            last_active: user.last_active,
            therapistIds: user.therapistIds
          };
        }
      }
      
      if (userData) {
        const role: UserRole = userData.role || 'standard_user';
        sessionStorage.setItem('activeUserRole', role);

        // Create MentraUser by extending the Firebase User object
        const mentraUser = Object.create(
          Object.getPrototypeOf(firebaseUser),
          Object.getOwnPropertyDescriptors(firebaseUser)
        ) as MentraUser;
        
        // Add Mentra-specific properties
        Object.assign(mentraUser, {
          role: role,
          free_access: userData.free_access || false,
          daily_token_limit: userData.daily_token_limit || 20000,
          tokens_used_today: userData.tokens_used_today || 0,
          content_mode: userData.content_mode || 'FULL' as ContentMode,
          theme_preference: userData.theme_preference || 'light' as Theme,
          created_at: userData.created_at,
          updated_at: userData.updated_at,
          therapist_request_pending: userData.therapist_request_pending || false,
          demo_enabled: userData.demo_enabled || false,
          last_active: userData.last_active,
          therapist_id: userData.therapist_id
        });
        
        setUser(mentraUser);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setError('Failed to refresh user data');
    } finally {
      setLoadingAuth(false);
    }
  };

  const updateUserRole = async (role: UserRole) => {
    if (user) {
      // 1. Update the local state immediately for a responsive UI
      setUser({ ...user, role });
      
      // Also update the role in session storage for the API service
      sessionStorage.setItem('activeUserRole', role);

      try {
        // 2. Update the role in the Firestore database
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, { role: role }, { merge: true });
      } catch (error) {
        console.error("Failed to update user role in database:", error);
        // Optionally, revert the local state if the DB update fails
        // For now, we'll just log the error.
        setError("Failed to save role change. Please refresh.");
      }
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setError('Failed to sign in');
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      // Clear parental state on logout
      localDbService.clearParentalState();
    } catch (error) {
      setError('Failed to sign out');
      console.error('Error signing out:', error);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Try to create the user document in Firestore
      try {
        const userRef = doc(db, 'users', userCredential.user.uid);
        const defaultUserData = {
          email: email,
          role: 'patient' as UserRole, // Changed from 'standard_user' to 'patient'
          free_access: false,
          daily_token_limit: 20000,
          tokens_used_today: 0,
          content_mode: 'FULL' as ContentMode,
          theme_preference: 'light' as Theme,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          therapist_request_pending: false,
          demo_enabled: false,
          last_active: new Date().toISOString(),
          therapist_id: null,
          therapistIds: []
        };
        await setDoc(userRef, defaultUserData);
        
        // Set the role in sessionStorage for API calls
        sessionStorage.setItem('activeUserRole', 'patient');
        
      } catch (firestoreError) {
        console.warn('Firestore user document creation failed:', firestoreError);
        // User is still created in Firebase Auth, just without Firestore document
        // This will be handled by the fetchAndSetUser function
      }
      
    } catch (error) {
      setError('Failed to create account');
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      setError('Failed to reset password');
      throw error;
    }
  };

  const value = {
    user,
    loading,
    loadingAuth,
    isInitialized,
    isAuthenticated: !!user,
    error,
    signIn,
    signOut,
    signUp,
    resetPassword,
    refreshUser,
    updateUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Named exports at the bottom
export { AuthProvider, useAuth }; 